/**
 * Workflow State Management
 * Zustand store for workflow and project state
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface Project {
  id: string;
  name: string;
  project_key: string;
  status: 'ACTIVE' | 'INACTIVE';
  description?: string;
  project_manager?: string;
  created_at: string;
}

export interface WorkflowStatus {
  projectId: string;
  currentStage: string;
  status: string;
  progressPercentage: number;
  completedStages: string[];
  nextStage?: string;
  estimatedCompletion?: string;
  lastActivity: string;
}

export interface Intervention {
  id: string;
  execution_id: string | null;
  type: string;
  status: 'PENDING' | 'IN_REVIEW' | 'RESOLVED' | 'ESCALATED' | 'EXPIRED';
  prompt: string;
  recommended_action?: string;
  assigned_to?: {
    id: string;
    name: string;
    type: string;
  };
  requesting_agent?: {
    id: string;
    name: string;
    type: string;
  };
  response?: Record<string, any>;
  created_at: string;
  resolved_at?: string;
  project_info?: {
    project_id: string;
    project_name: string;
    project_key: string;
  };
  /** Release overlay for quota advisories (capacity_warning / budget_warning). */
  release_info?: {
    id: string;
    release_ref?: string;
    display_name?: string | null;
  };
}

export interface SystemAlert {
  level: 'INFO' | 'WARNING' | 'ERROR';
  message: string;
  component: string;
  timestamp: string;
}

interface WorkflowStore {
  // Projects
  projects: Project[];
  selectedProject: Project | null;
  
  // Workflows
  workflowStatuses: Map<string, WorkflowStatus>;
  
  // Interventions
  interventions: Intervention[];
  pendingInterventions: Intervention[];
  
  // System
  systemAlerts: SystemAlert[];
  dismissedSystemAlertKeys: string[];
  isConnected: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error';

  // Workflow Stages Filters (persisted across navigation).
  // Project/Status/Priority are multi-select (arrays); empty array = "All".
  stagesProjectFilter: string[];
  stagesSearchText: string;
  stagesStatusFilter: string[];
  stagesPriorityFilter: string[];
  
  // Actions
  setProjects: (projects: Project[]) => void;
  setSelectedProject: (project: Project | null) => void;
  updateWorkflowStatus: (projectId: string, status: WorkflowStatus) => void;
  addIntervention: (intervention: Intervention) => void;
  updateIntervention: (interventionId: string, updates: Partial<Intervention>) => void;
  removeIntervention: (interventionId: string) => void;
  addSystemAlert: (alert: SystemAlert) => void;
  dismissSystemAlert: (alert: SystemAlert) => void;
  removeSystemAlertsByComponent: (component: string) => void;
  clearSystemAlerts: () => void;
  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting' | 'error') => void;
  setStagesProjectFilter: (ids: string[]) => void;
  setStagesSearchText: (text: string) => void;
  setStagesStatusFilter: (statuses: string[]) => void;
  setStagesPriorityFilter: (priorities: string[]) => void;
  clearStagesFilters: () => void;
  
  // Computed getters
  getProjectWorkflowStatus: (projectId: string) => WorkflowStatus | undefined;
  getPendingInterventionsForProject: (projectId: string) => Intervention[];
  getUserInterventions: (userId: string) => Intervention[];
}

export const useWorkflowStore = create<WorkflowStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      projects: [],
      selectedProject: null,
      workflowStatuses: new Map(),
      interventions: [],
      pendingInterventions: [],
      systemAlerts: [],
      dismissedSystemAlertKeys: [],
      isConnected: false,
      connectionStatus: 'disconnected',
      stagesProjectFilter: [],
      stagesSearchText: '',
      stagesStatusFilter: [],
      stagesPriorityFilter: [],

      // Actions
      setProjects: (projects) => 
        set({ projects }, false, 'setProjects'),

      setSelectedProject: (project) => 
        set({ selectedProject: project }, false, 'setSelectedProject'),

      updateWorkflowStatus: (projectId, status) =>
        set((state) => {
          const newStatuses = new Map(state.workflowStatuses);
          newStatuses.set(projectId, status);
          return { workflowStatuses: newStatuses };
        }, false, 'updateWorkflowStatus'),

      addIntervention: (intervention) =>
        set((state) => {
          const newInterventions = [...state.interventions, intervention];
          const newPending = intervention.status === 'PENDING' 
            ? [...state.pendingInterventions, intervention]
            : state.pendingInterventions;
          
          return {
            interventions: newInterventions,
            pendingInterventions: newPending,
          };
        }, false, 'addIntervention'),

      updateIntervention: (interventionId, updates) =>
        set((state) => {
          const newInterventions = state.interventions.map(intervention =>
            intervention.id === interventionId
              ? { ...intervention, ...updates }
              : intervention
          );
          
          const newPending = newInterventions.filter(i => i.status === 'PENDING');
          
          return {
            interventions: newInterventions,
            pendingInterventions: newPending,
          };
        }, false, 'updateIntervention'),

      removeIntervention: (interventionId) =>
        set((state) => ({
          interventions: state.interventions.filter(i => i.id !== interventionId),
          pendingInterventions: state.pendingInterventions.filter(i => i.id !== interventionId),
        }), false, 'removeIntervention'),

      addSystemAlert: (alert) =>
        set((state) => {
          const dismissKey = `${alert.component}:${alert.timestamp}`;
          if (state.dismissedSystemAlertKeys.includes(dismissKey)) return state;
          const alertKey = `${alert.level}:${alert.component}:${alert.message}`;
          const isDuplicate = state.systemAlerts.some(
            (a) => `${a.level}:${a.component}:${a.message}` === alertKey
          );
          if (isDuplicate) return state;
          const newAlerts = [alert, ...state.systemAlerts].slice(0, 50);
          return { systemAlerts: newAlerts };
        }, false, 'addSystemAlert'),

      dismissSystemAlert: (alert) =>
        set((state) => {
          const dismissKey = `${alert.component}:${alert.timestamp}`;
          return {
            systemAlerts: state.systemAlerts.filter(
              (a) => !(a.component === alert.component && a.timestamp === alert.timestamp)
            ),
            dismissedSystemAlertKeys: state.dismissedSystemAlertKeys.includes(dismissKey)
              ? state.dismissedSystemAlertKeys
              : [...state.dismissedSystemAlertKeys, dismissKey].slice(-100),
          };
        }, false, 'dismissSystemAlert'),

      removeSystemAlertsByComponent: (component) =>
        set((state) => ({
          systemAlerts: state.systemAlerts.filter((a) => a.component !== component),
        }), false, 'removeSystemAlertsByComponent'),

      clearSystemAlerts: () =>
        set({ systemAlerts: [], dismissedSystemAlertKeys: [] }, false, 'clearSystemAlerts'),

      setConnectionStatus: (status) =>
        set({ 
          connectionStatus: status,
          isConnected: status === 'connected'
        }, false, 'setConnectionStatus'),

      setStagesProjectFilter: (ids) =>
        set({ stagesProjectFilter: ids }, false, 'setStagesProjectFilter'),
      setStagesSearchText: (text) =>
        set({ stagesSearchText: text }, false, 'setStagesSearchText'),
      setStagesStatusFilter: (statuses) =>
        set({ stagesStatusFilter: statuses }, false, 'setStagesStatusFilter'),
      setStagesPriorityFilter: (priorities) =>
        set({ stagesPriorityFilter: priorities }, false, 'setStagesPriorityFilter'),
      clearStagesFilters: () =>
        set({
          stagesProjectFilter: [],
          stagesSearchText: '',
          stagesStatusFilter: [],
          stagesPriorityFilter: [],
        }, false, 'clearStagesFilters'),

      // Computed getters
      getProjectWorkflowStatus: (projectId) => {
        return get().workflowStatuses.get(projectId);
      },

      getPendingInterventionsForProject: (projectId) => {
        return get().pendingInterventions.filter(
          intervention => intervention.project_info?.project_id === projectId
        );
      },

      getUserInterventions: (userId) => {
        return get().interventions.filter(
          intervention => intervention.assigned_to?.id === userId
        );
      },
    }),
    {
      name: 'workflow-store',
    }
  )
);

export default useWorkflowStore;